/**
 * CritiqueSessionListItem Component
 * Feature: 055-my-critiques-page
 *
 * Displays a single critique session card in the My Critiques list.
 * Shows collection hero image, session title, and up to 5 recent comments.
 */

import { useNavigate } from 'react-router-dom';
import type { CritiqueSessionListItem as CritiqueSessionListItemType } from '../../../types/critique-session.types';
import { critiqueSessionUrl } from '../../../utils/urls';
import { CommentPreview } from './CommentPreview';
import { Link } from '@/components/atoms/Link';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { AISessionBadge } from '@/components/ui/AISessionBadge';
import logger from '../../../utils/logger';
import { collectionEditUrl, userProfileUrl } from '@/utils/urls';
import { useAuth } from '@/services/auth';

export interface CritiqueSessionListItemProps {
  session: CritiqueSessionListItemType;
  onClick?: (sessionId: string, slug: string) => void;
  /** Variant controls display: 'my-critiques' shows "for: Collection", 'recent' shows only byline with linked title */
  variant?: 'my-critiques' | 'recent';
}

/**
 * Session card component
 *
 * Displays a clickable card with:
 * - Collection hero image (left)
 * - Session title and metadata (right top)
 * - Recent comments list (right bottom, max 5)
 *
 * @param session - Session data to display
 * @param onClick - Optional click handler (sessionId, slug)
 *
 * @example
 * ```tsx
 * <CritiqueSessionListItem
 *   session={sessionData}
 *   onClick={(id, slug) => navigate(`/crit/${id}/${slug}`)}
 * />
 * ```
 */
export function CritiqueSessionListItem({ session, onClick, variant = 'my-critiques' }: CritiqueSessionListItemProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Show "by <username>" when current user is not the collection owner
  const showOwnerByline = session.collection.owner && user?.id !== session.collection.owner.id;

  const handleClick = () => {
    const url = critiqueSessionUrl(session.globalId, session.slug);
    logger.info('CritiqueSessionListItem', 'Navigating to session', {
      sessionId: session.globalId,
      slug: session.slug,
      url,
    });

    if (onClick) {
      onClick(session.globalId, session.slug);
    } else {
      navigate(url);
    }
  };

  // Limit to 6 comments (for balanced 2-column grid)
  const displayComments = session.recentComments.slice(0, 6);

  return (
    <div
      role="button"
      onClick={handleClick}
      className="cursor-pointer bg-surface-1 border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 flex gap-4 h-[33vh] overflow-hidden"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Left: Hero Image */}
      <div className="flex-shrink-0 w-[30%] flex flex-col">
        {session.collection.heroPhoto ? (
          <AdaptivePhoto
            photo={session.collection.heroPhoto}
            alt={session.collection.title}
            className="w-full h-full rounded overflow-hidden"
          />
        ) : (
          <div className="w-full aspect-square bg-surface-2 rounded flex items-center justify-center">
            <span className="text-muted text-sm">No image</span>
          </div>
        )}
      </div>

      {/* Right: Session Info and Comments */}
      <div className="flex-grow min-w-0 overflow-y-auto">
        {/* Session Title and Collection */}
        <div className="mb-3">
          <div className="flex items-center gap-2">
            {variant === 'recent' ? (
              <Link
                to={critiqueSessionUrl(session.globalId, session.slug)}
                onClick={(e) => e.stopPropagation()}
                className="text-lg font-semibold text-primary hover:underline"
              >
                {session.title}
              </Link>
            ) : (
              <h3 className="text-lg font-semibold text-foreground">{session.title}</h3>
            )}
            {/* Feature 071: AI session indicator badge */}
            <AISessionBadge isAiSession={session.isAiSession} />
          </div>
          {variant === 'my-critiques' ? (
            <p>
              for:{' '}
              <Link
                to={collectionEditUrl(session.collection.globalId!, session.collection.slug)}
                onClick={(e) => e.stopPropagation()}
              >
                {session.collection.title}
              </Link>
              {showOwnerByline && session.collection.owner && (
                <>
                  {' '}by{' '}
                  <Link
                    to={userProfileUrl(session.collection.owner.username)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {session.collection.owner.displayName || session.collection.owner.username}
                  </Link>
                </>
              )}
            </p>
          ) : (
            showOwnerByline && session.collection.owner && (
              <p>
                by{' '}
                <Link
                  to={userProfileUrl(session.collection.owner.username)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {session.collection.owner.displayName || session.collection.owner.username}
                </Link>
              </p>
            )
          )}
        </div>

        {/* Recent Comments - Newspaper-style columns */}
        <div className="columns-1 md:columns-2 gap-4 space-y-4">
          {displayComments.length === 0 ? (
            <p className="text-sm text-muted italic">No comments yet</p>
          ) : (
            displayComments.map((comment) => (
              <div key={comment.globalId} className="break-inside-avoid mb-4">
                <CommentPreview
                  comment={comment}
                  sessionId={session.globalId}
                  sessionSlug={session.slug}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
